// An Audiotool project as a Seedbed seed: AudiotoolProjection → Snapshot.
//
// The closing half of the round trip. Push sends a seed to Audiotool; this
// brings an Audiotool project back into the Crate, as a new root seed or a
// branch off the one it came from — so a producer who doesn't own Live can
// still put something in.
//
// It is planPull with a different destination. Pulling into Live and capturing
// into a seed ask the same question — what does this project actually play? —
// so the answer is computed once, in pull-plan.ts, and shaped here. Working
// from the plan rather than the document means the tick maths, the loop
// unrolling, the MIDI flattening and the mixer walk are the tested ones, and
// the two directions can never drift apart.
//
// Audio is not referenced, it is CUT. A seed's clip owns its own audio, so
// each clip becomes the slice of the sample it plays (pull-plan works in
// sample fractions; shared/audio/slice.ts makes the verbatim cut). Two clips
// that play the same window of the same sample cut the same bytes and land on
// one object in the blob pool.
//
// Pure: no I/O, no SDK. The caller downloads, cuts, hashes and uploads.

import { NO_CLIP_COLOUR } from "../clip-colour.js";
import { notesDigest } from "../midi/notes.js";
import type { AudioClipInfo, AudioTrackInfo, MidiClipInfo, MidiTrackInfo, Snapshot, TrackInfoBase } from "../seed-history.js";
import { planPull, type PullMidiTrack, type PullTrackSettings, type PullWarning } from "./pull-plan.js";
import { foldSoundLanes } from "./sound-lanes.js";
import type { AudiotoolProjection, ProjSample } from "./projection.js";

/**
 * The audio one arrangement clip needs: a window of one sample. `trackIndex`
 * and `clipIndex` address `snapshot.audioTracks[t].arrangementClips[c]` — the
 * same join keys the uploads API's `clipAudio` uses.
 */
export interface CaptureClipAudio {
  trackIndex: number;
  clipIndex: number;
  sampleName: string;
  fromFraction: number;
  toFraction: number;
  /**
   * Audiotool plays this window over a different length than the sample's own
   * — a pitch-preserving time-stretch (checked against its renderer,
   * tools/audiotool-stretch-spike.ts). A verbatim cut would be the wrong
   * length and tempo, so this clip's audio should come from Audiotool's render
   * of the track when there is one.
   */
  stretched: boolean;
  /**
   * Anything that makes Audiotool play this clip other than as its sample,
   * verbatim — stretch, speed curve, region gain, fades, pitch shift, or
   * effects/automation on its track's way to the mixer. Such a
   * clip's audio should come from Audiotool's render of the track.
   */
  altered: boolean;
}

/** An Audiotool track behind a snapshot track: what a per-track render isolates. */
export interface CaptureTrackSource {
  /** The Audiotool track entity. */
  trackId: string;
  /** Its mixer channel; null when it isn't cabled to one (it plays silent). */
  stripId: string | null;
  /** A MIDI track whose sound came in on a companion lane: the ids are that lane's. */
  paired?: boolean;
}

export interface SeedPlan {
  /** Ready but for `audioKey`, which only exists once the bytes are hashed. */
  snapshot: Snapshot;
  /** Distinct samples to download, in first-use order. */
  samples: ProjSample[];
  clipAudio: CaptureClipAudio[];
  /**
   * Bounces of MIDI clips carried on a sound lane (sound-lanes.ts), addressed
   * into `snapshot.midiTracks` — the seed's own bounce bytes, when unaltered.
   */
  midiBounces: CaptureClipAudio[];
  /** Per snapshot track, the Audiotool track it came from — same order as `snapshot.audioTracks` / `midiTracks`. */
  sources: { audio: CaptureTrackSource[]; midi: CaptureTrackSource[] };
  warnings: PullWarning[];
  tempo: number;
}

/**
 * A captured clip carries no colour — Audiotool regions have none — so both
 * fields say the same neutral grey rather than one of them inventing a colour
 * Live would then show.
 */
const NO_COLOUR = NO_CLIP_COLOUR;

export function planSeedFromProjection(p: AudiotoolProjection): SeedPlan {
  // "match": the seed runs at the project's own tempo, so no clip is stretched
  // and every position is the project's. liveTempo is the fallback for a
  // project with no config yet, which planPull warns about.
  // A MIDI track that went out with its sound on a companion lane comes back
  // as one track again (sound-lanes.ts).
  const plan = foldSoundLanes(planPull(p, { liveTempo: p.config?.tempoBpm ?? 120, tempoChoice: "match" }));

  const clipAudio: CaptureClipAudio[] = [];
  const audioTracks: AudioTrackInfo[] = plan.audioTracks.map((t, trackIndex) => ({
    ...base(t.name, t.settings),
    arrangementClips: t.clips.map((c, clipIndex) => {
      clipAudio.push({
        trackIndex,
        clipIndex,
        sampleName: c.sampleName,
        fromFraction: c.fromFraction,
        toFraction: c.toFraction,
        stretched: c.stretched,
        // Effects or automation on the track change every clip on it.
        altered: c.altered || t.processed,
      });
      return audioClip(c.name, c.startTime, c.duration, c.muted);
    }),
    takeLanes: [],
  }));

  const midiTracks: MidiTrackInfo[] = plan.midiTracks.map((t) => ({
    ...base(t.name, t.settings),
    kind: "midi" as const,
    arrangementClips: t.clips.map((c): MidiClipInfo => ({
      ...span(c.name, c.startTime, c.duration, c.muted),
      notes: c.notes,
      noteCount: c.notes.length,
      notesDigest: notesDigest(c.notes),
    })),
  }));

  const arrangementClipCount = audioTracks.reduce((n, t) => n + t.arrangementClips.length, 0);
  const midiClipCount = midiTracks.reduce((n, t) => n + t.arrangementClips.length, 0);

  const snapshot: Snapshot = {
    song: {
      tempo: plan.tempo,
      gridQuantization: { value: 0, label: "", isTriplet: false },
      // Audiotool holds no key or scale, so this says "unknown" rather than
      // guessing C major and having Live's scale-aware tools believe it.
      scale: { rootNote: 0, rootNoteName: "C", name: "", mode: false, intervals: [] },
      cuePoints: [],
    },
    audioTracks,
    ...(midiTracks.length > 0 ? { midiTracks } : {}),
    totals: {
      audioTrackCount: audioTracks.length,
      arrangementClipCount,
      takeLaneClipCount: 0,
      ...(midiTracks.length > 0 ? { midiTrackCount: midiTracks.length, midiClipCount } : {}),
    },
  };

  const source = (t: { id: string; stripId: string | null }): CaptureTrackSource => ({ trackId: t.id, stripId: t.stripId });
  // A paired MIDI track's audio is its sound lane: that is what a render of
  // "the track" must isolate, and where its bounces are cut from.
  const midiSource = (t: PullMidiTrack, i: number): CaptureTrackSource => {
    const sound = plan.soundOf[i];
    return sound ? { trackId: sound.id, stripId: sound.stripId, paired: true } : source(t);
  };
  const midiBounces: CaptureClipAudio[] = plan.bounces.map((b) => ({
    trackIndex: b.midiTrackIndex,
    clipIndex: b.clipIndex,
    sampleName: b.clip.sampleName,
    fromFraction: b.clip.fromFraction,
    toFraction: b.clip.toFraction,
    stretched: b.clip.stretched,
    altered: b.clip.altered || (plan.soundOf[b.midiTrackIndex]?.processed ?? false),
  }));
  return {
    snapshot,
    samples: plan.samples,
    clipAudio,
    midiBounces,
    sources: { audio: plan.audioTracks.map(source), midi: plan.midiTracks.map(midiSource) },
    warnings: plan.warnings,
    tempo: plan.tempo,
  };
}

function base(name: string, s: PullTrackSettings): TrackInfoBase {
  return {
    name,
    mute: s.mute,
    solo: s.solo,
    arm: s.arm,
    mutedViaSolo: false,
    volume: s.volume,
    panning: s.panning,
    groupTrackName: null,
  };
}

/** The timing every captured clip shares: a plain, unlooped span. */
function span(name: string, startTime: number, duration: number, muted: boolean) {
  return {
    name,
    startTime,
    endTime: startTime + duration,
    duration,
    startMarker: 0,
    endMarker: duration,
    // A captured clip is its own slice of audio, cut to length — the loop and
    // the repetitions were already unrolled into separate clips by pull-plan.
    looping: false,
    loopStart: 0,
    loopEnd: duration,
    color: NO_COLOUR,
    colorHex: `#${NO_COLOUR.toString(16).padStart(6, "0")}`,
    muted,
  };
}

function audioClip(name: string, startTime: number, duration: number, muted: boolean): AudioClipInfo {
  return {
    ...span(name, startTime, duration, muted),
    // No producer's local file is behind this: the audio came from Audiotool.
    filePath: "",
    // Audiotool does not time-stretch, and the slice is already the right
    // length at this tempo, so the clip plays unwarped.
    warping: false,
    warpMode: { value: 0, name: "Beats" },
    warpMarkerCount: 0,
    audioKey: null,
  };
}
